import { DisplayLabel, GenericTable, Pagination, Text, TextCell, cn, formatDate } from '$components'
import { SmsCreditTransactionType, type SmsCreditTransaction } from '$api-client/types.gen'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import { TableSkeleton } from '$components'

type SmsCreditsTransactionHistoryProps = {
  transactions: SmsCreditTransaction[]
  isLoading: boolean
  currentPage: number
  totalPages: number
  totalItems: number
  onPageChange: (page: number) => void
}

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  subscription_grant: 'Plan allocation',
  purchased_grant: 'Credit purchase',
  deduction: 'SMS sent',
  refund: 'Refund',
}

const columnHelper = createColumnHelper<SmsCreditTransaction>()

function formatTransactionAmount(transaction: SmsCreditTransaction): string {
  if (transaction.type === SmsCreditTransactionType.DEDUCTION) {
    return `-${transaction.amount}`
  }

  return `+${transaction.amount}`
}

export function SmsCreditsTransactionHistory({
  transactions,
  isLoading,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}: SmsCreditsTransactionHistoryProps) {

  const columns = useMemo(() => [
    columnHelper.accessor('created_at', {
      header: 'Date',
      cell: info => (
        <TextCell>
          {formatDate(info.getValue())}
        </TextCell>
      ),
    }),
    columnHelper.accessor('type', {
      header: 'Type',
      cell: info => (
        <TextCell>
          {TRANSACTION_TYPE_LABELS[info.getValue()] ?? info.getValue()}
        </TextCell>
      ),
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: info => (
        <TextCell>
          {info.getValue() ?? '—'}
        </TextCell>
      ),
    }),
    columnHelper.accessor('amount', {
      header: 'Amount',
      cell: info => (
        <Text
          type="s"
          className={cn('text-green-600', {
            'text-red-600': info.row.original.type === SmsCreditTransactionType.DEDUCTION,
          })}
        >
          {formatTransactionAmount(info.row.original)}
        </Text>
      ),
    }),
  ], [])

  return (
    <div>
      <DisplayLabel>Transaction history</DisplayLabel>
      <Text
        type="s"
        className="text-gray-500"
      >
        View past credit purchases and SMS usage
      </Text>

      <div className="mt-4">
        {isLoading && (
          <TableSkeleton
            columns={4}
            rows={5}
          />
        )}

        {!isLoading && transactions.length === 0 && (
          <Text
            type="s"
            className="text-gray-500"
          >
            No transactions yet.
          </Text>
        )}

        {!isLoading && transactions.length > 0 && (
          <GenericTable.Container>
            <GenericTable
              data={transactions}
              columns={columns}
            />

            <Pagination
              totalItems={totalItems}
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={onPageChange}
            />
          </GenericTable.Container>
        )}
      </div>
    </div>
  )
}
